import { format } from 'date-fns';

// Custom "from → to" window (inclusive, local yyyy-MM-dd) shared by the
// per-layer time filters. Each layer caps `from` at its own data window
// (valuations tiles carry 3 months, sales 2 years, …).
export type CustomDateRange = { from: string; to: string };

export const todayIso = (): string => format(new Date(), 'yyyy-MM-dd');

export const daysAgoIso = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return format(date, 'yyyy-MM-dd');
};

export const monthsAgoIso = (months: number): string => {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  return format(date, 'yyyy-MM-dd');
};

// Exclusive upper bound for filtering ISO *timestamps* by a date-only `to`
// (lexicographic `< nextDay` is safer than `<= to + 'T23:59…'` suffixes).
export const nextDayIso = (date: string): string => {
  const parsed = new Date(`${date}T12:00:00Z`);
  parsed.setUTCDate(parsed.getUTCDate() + 1);
  return parsed.toISOString().slice(0, 10);
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const isIsoDate = (raw: string): boolean => DATE_RE.test(raw);

// URL param format: `<from>_<to>`. Bounds outside [minDate, today] mean a
// stale shared link — clamp rather than drop so the intent survives.
export const parseCustomRangeParam = (
  raw: string | null,
  minDate?: string,
): CustomDateRange | null => {
  if (raw == null) {
    return null;
  }
  const parts = raw.split('_');
  if (parts.length !== 2 || !parts.every(isIsoDate)) {
    return null;
  }
  const today = todayIso();
  let [from, to] = parts as [string, string];
  if (minDate != null && from < minDate) {
    from = minDate;
  }
  if (to > today) {
    to = today;
  }
  return from <= to ? { from, to } : null;
};

export const customRangeParam = (range: CustomDateRange): string =>
  `${range.from}_${range.to}`;
